import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";
import PriceTrend from "@/components/priceTrend";
import {
  ResponsiveChartContainer,
  LinePlot,
  useDrawingArea,
  useXScale,
  useYScale,
} from "@mui/x-charts";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { isEmpty } from "lodash";

import { selectLocalization } from "@/store/store/selectors";

import { getStoreAddressString } from "@/utils/otherUtil";
import { getLocalisationVal } from "@/utils/localizationUtil";

import { CommonFonts, Colors } from "@/theme";
import useStyles from "./styles";

const RECT_WIDTH = 43;
const RECT_HEIGHT = 16;

const StyledText = styled("text")(({ theme }) => ({
  ...CommonFonts.darkBoldFont10,
  fill: Colors.GRAY_DARK,
}));

const StyledRect = styled("rect")(({ theme }) => ({
  width: RECT_WIDTH,
  height: RECT_HEIGHT,
  fill: Colors.WHITE,
  stroke: Colors.GRAY_BORDER,
}));

function DrawingAreaBox({ mark, localization }) {
  // Get the two scale
  const yAxisScale = useYScale();
  const xAxisScale = useXScale();

  const xData = [...mark.history_times, moment(mark.last_updated)];
  const yData = [...mark.history_prices, mark.y];

  return xData.map((x, id) => {
    const isLargerThanBefore = yData[id] >= yData?.[id - 1];

    const yOrigin = isLargerThanBefore
      ? yAxisScale(yData[id]) - 5
      : yAxisScale(yData[id]) + 18;
    const xOrigin = xAxisScale(x);
    return (
      <React.Fragment key={`drawing-${id}`}>
        <StyledRect
          x={xOrigin - RECT_WIDTH / 2}
          y={yOrigin - RECT_HEIGHT + 2}
          rx={10}
        />
        <StyledText
          x={xOrigin}
          y={yOrigin}
          textAnchor="middle"
          dominantBaseline="text-after-edge"
        >
          {getLocalisationVal(localization, yData[id])}
        </StyledText>
      </React.Fragment>
    );
  });
}

const CustomItemTooltipContent = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { itemData, series } = props;
  const localization = useSelector(selectLocalization);

  const mark = series.data[itemData.dataIndex];

  const config = {
    xAxis: [
      {
        data: [...mark.history_times, moment(mark.last_updated)],
        scaleType: "time",
      },
    ],
    series: [
      {
        type: "line",
        curve: "stepAfter",
        data: [...mark.history_prices, mark.y],
        color: Colors.RED,
        showMark: false,
      },
    ],
    height: 150,

    sx: {
      "& .MuiLineElement-root": {
        strokeWidth: 3,
      },
    },
  };
  return (
    <Paper
      sx={{
        padding: 3,
        backgroundColor: Colors.GREEN_LIGHT,
        width: 300,
        borderRadius: 4,
        borderColor: Colors.GRAY_BORDER,
        borderWidth: 1,
      }}
    >
      <Box display={"flex"} justifyContent={"space-between"}>
        <img src={mark.store?.brand?.logo?.thumbnail} width={50} height={50} />
        <Box>
          <Box display={"flex"}>
            <Box mr={2}>
              <PriceTrend fontSize={14} trend={mark.trend} />
            </Box>

            <span className={classes.storeNameText}>
              {getLocalisationVal(localization, mark.y)}
              <span style={{ fontSize: 10 }}>p</span>
            </span>
          </Box>
          <Box textAlign={"right"} mt={1}>
            <div className={classes.addressText}>
              {moment.utc(mark.last_updated).local().format("DD/MM HH:mm")}
            </div>
            <div className={classes.lastUpdatedText}>
              {t("manage_price_screen.last_updated")}
            </div>
          </Box>
        </Box>
      </Box>
      <Box mt={4}>
        <span className={classes.storeNameText}>{mark.store.name}</span>
        <Box mt={1}>
          <span className={classes.addressText}>
            {getStoreAddressString(mark.store?.address)}
          </span>
        </Box>
        <Box>
          <span className={classes.addressText}>
            {`${getLocalisationVal(localization, mark.x)} mi away`}
          </span>
        </Box>
      </Box>
      {!isEmpty(mark.history_prices) && (
        <Box>
          <ResponsiveChartContainer {...config}>
            <LinePlot />
            <DrawingAreaBox mark={mark} localization={localization} />
          </ResponsiveChartContainer>
        </Box>
      )}
    </Paper>
  );
};

export default CustomItemTooltipContent;
